import { useState } from 'react';
import { palette } from '../../styles/tokens';
import { useCreateEvent } from '../../hooks/useCreateEvent';
import { useAuthStore } from '../../stores/authStore';
import { DatePicker } from '../common/DatePicker';
import { TimePicker } from '../common/TimePicker';

interface Props { onCreated: () => void; onCancel: () => void; }

const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const FIELD: React.CSSProperties = { marginBottom: 16 };

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div onClick={() => onChange(!on)} style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: on ? palette.accent : palette.g300, padding: 2, transition: 'background 0.15s', display: 'flex', alignItems: 'center' }}>
      <div style={{ width: 16, height: 16, borderRadius: 8, background: palette.white, transition: 'margin-left 0.15s', marginLeft: on ? 16 : 0 }} />
    </div>
  );
}

export function CreateEventForm({ onCreated, onCancel }: Props) {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const { createEvent, loading, error } = useCreateEvent();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('everyone');
  const [recurrence, setRecurrence] = useState('none');

  const handleSubmit = async () => {
    if (!business || !user) return;
    const ok = await createEvent({
      business_id: business.id, title, date, all_day: allDay,
      start_time: allDay ? undefined : startTime, end_time: allDay ? undefined : endTime,
      description, visibility, recurrence, created_by: user.id,
    });
    if (ok) onCreated();
  };

  return (
    <div>
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}
      <div style={FIELD}><label style={LABEL}>Title *</label><input style={INPUT} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Team Meeting" /></div>
      <div style={FIELD}><label style={LABEL}>Date *</label>
        <DatePicker value={date || null} onChange={(v) => setDate(v ?? '')} />
      </div>
      <div style={{ ...FIELD, display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ ...LABEL, marginBottom: 0 }}>All day</label>
        <Toggle on={allDay} onChange={setAllDay} />
      </div>
      <div style={{ display: 'flex', gap: 12, ...FIELD }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Start</label>
          <TimePicker value={startTime || null} onChange={(v) => setStartTime(v ?? '')} step={15} disabled={allDay} />
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>End</label>
          <TimePicker value={endTime || null} onChange={(v) => setEndTime(v ?? '')} step={15} disabled={allDay} minTime={!allDay && startTime ? startTime : undefined} />
        </div>
      </div>
      {allDay && (
        <div style={{ ...FIELD, marginTop: -8, fontSize: 12, color: palette.g500 }}>All day — times not required</div>
      )}
      <div style={FIELD}><label style={LABEL}>Description</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} /></div>
      <div style={FIELD}><label style={LABEL}>Visibility</label><div style={{ display: 'flex', gap: 16 }}>
        {[{ v: 'everyone', l: 'Everyone' }, { v: 'managers', l: 'Managers only' }, { v: 'owner_only', l: 'Owner only' }].map(o => (
          <label key={o.v} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13 }}><input type="radio" name="vis" checked={visibility === o.v} onChange={() => setVisibility(o.v)} />{o.l}</label>
        ))}
      </div></div>
      <div style={FIELD}><label style={LABEL}>Recurrence</label><select style={INPUT} value={recurrence} onChange={e => setRecurrence(e.target.value)}>
        {['none', 'daily', 'weekly', 'biweekly', 'monthly'].map(r => <option key={r} value={r}>{r === 'none' ? 'None' : r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
      </select></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onCancel} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSubmit} disabled={loading} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: loading ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{loading ? 'Creating...' : 'Create Event'}</button>
      </div>
    </div>
  );
}
